import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router";
import { chatChannelsAdd, chatChannelsInformations } from "../../Api/types";
import { fetchMembersFromIds } from "../../Api/member";
import { api_postValueOrErrorMessage } from "../../Api/util";
import useIsMobile from "../../Hook/mobile";
import { OPEN_CHANNELS } from "../../Hook/openChannels";
import { closeCurrentToast, ToastDescriptor } from "../../Hook/toast";
import useUser from "../../Hook/user";
import Avatar from "../Avatar";
import Pseudonym from "../Member/Pseudonym";

const LIFETIME = 10_000;

/**
 * The toast (Shared chrome board, turn 1a): a panel card at sheet radius whose
 * ten seconds retreat as an accent hairline along the bottom — flame on the
 * error variant, which is the only one allowed near that colour. Four jobs, one
 * shape: a message you can answer, a profile visit, a plain info line, an error
 * with one retry. Hovering pauses the countdown on desktop.
 */
function AppToast({ toast }: { toast: ToastDescriptor }) {
  const user = useUser();
  const navigate = useNavigate();
  const detectMobile = useIsMobile();
  // RF7: the countdown is one CSS animation and one pausable timeout — no
  // interval, no per-tick re-render, and the close happens in a timer callback,
  // never inside a state updater.
  const [paused, setPaused] = useState(false);
  const remaining = useRef(LIFETIME);
  const startedAt = useRef(Date.now());
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    remaining.current = LIFETIME;
    startedAt.current = Date.now();
    setPaused(false);
    timer.current = setTimeout(closeCurrentToast, LIFETIME);
    return () => clearTimeout(timer.current);
  }, [toast.id]);

  const pause = () => {
    clearTimeout(timer.current);
    remaining.current -= Date.now() - startedAt.current;
    setPaused(true);
  };
  const resume = () => {
    startedAt.current = Date.now();
    timer.current = setTimeout(closeCurrentToast, Math.max(0, remaining.current));
    setPaused(false);
  };

  const isError = toast.kind === "error";

  const openMessage = () => {
    api_postValueOrErrorMessage<chatChannelsAdd>("chat/channels/add", {
      members: [toast.member.id],
    }).then((channel) => {
      if (channel.error_message) return;
      api_postValueOrErrorMessage<chatChannelsInformations>("chat/channels/informations", {
        channels: [channel.channel],
      }).then(async (chan) => {
        if (chan.error_message) return;
        const currentChan = chan.channels[0];
        const otherMember = currentChan.members.find((e) => e !== user?.informations?.id);
        const member = await fetchMembersFromIds([otherMember as string]);
        currentChan.otherMember = member[0];
        OPEN_CHANNELS.openChannel(currentChan);
      });
    });
  };

  const openBody = () => {
    if (toast.kind === "message") openMessage();
    if (toast.kind === "visit") navigate("/member/" + toast.member.id);
    if (toast.kind === "info") toast.onOpen?.();
    if (toast.kind === "error") return;
    closeCurrentToast();
  };

  return (
    <div
      data-testid="toast"
      role="status"
      onClick={openBody}
      onMouseEnter={() => {
        if (!detectMobile.isMobile) pause();
      }}
      onMouseLeave={() => {
        if (paused) resume();
      }}
      className="relative w-full lg:w-96 overflow-hidden rounded-sheet bg-panel shadow-sheet cursor-pointer"
    >
      <div className="flex items-center gap-3 p-3 pr-11">
        {(toast.kind === "message" || toast.kind === "visit") && (
          <div className="w-11 h-11 flex-none rounded-pill overflow-hidden bg-well">
            <Avatar member={toast.member} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          {toast.member ? (
            <>
              <div className="flex items-baseline gap-2 min-w-0">
                <Pseudonym member={toast.member} />
                {toast.kind === "visit" && (
                  <span className="text-meta text-muted2 truncate">
                    <FormattedMessage id="toast__profile_visit_title" />
                  </span>
                )}
              </div>
              {toast.message && (
                <div className="text-body text-text truncate">{toast.message}</div>
              )}
              {toast.kind === "message" && (
                <div className="text-meta text-accent mt-0.5">
                  <FormattedMessage id="toast__reply" />
                </div>
              )}
            </>
          ) : (
            <>
              <div className={"text-body font-medium " + (isError ? "text-flame" : "text-text")}>
                {toast.title}
              </div>
              {toast.message && <div className="text-meta text-muted">{toast.message}</div>}
            </>
          )}
        </div>
        {isError && toast.retry && (
          <button
            type="button"
            data-testid="toast-retry"
            onClick={(event) => {
              event.stopPropagation();
              closeCurrentToast();
              toast.retry?.();
            }}
            className="flex-none px-3 py-1.5 rounded-pill ring-1 ring-inset ring-flame text-flame text-meta font-medium"
          >
            <FormattedMessage id="toast__retry" />
          </button>
        )}
      </div>

      <button
        type="button"
        data-testid="toast-close"
        onClick={(event) => {
          event.stopPropagation();
          closeCurrentToast();
        }}
        className="absolute top-2 right-2 w-7 h-7 rounded-pill text-muted flex items-center justify-center hover:bg-tint-hover"
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>

      {/* The lifetime, drawn: an accent hairline retreating along the bottom —
          a compositor-side animation, paused with the countdown on hover. */}
      <div
        className={"s4s-toast-drain absolute bottom-0 left-0 h-0.5 " + (isError ? "bg-flame" : "bg-accent")}
        style={{ animationDuration: LIFETIME + "ms", animationPlayState: paused ? "paused" : "running" }}
        aria-hidden
      />
    </div>
  );
}

export default AppToast;
